import {Injectable} from "@angular/core";
import {HttpClient, HttpParams, HttpResponse} from "@angular/common/http";
import {catchError, map, Observable, of} from "rxjs";
import {environment} from "../../environments/environment";
import {UserCard} from "../interface/userCard";
import {UserCardResponse} from "../interface/userCardResponse";
import {CreateUserCard} from "../interface/createUserCard";
import {ImageType} from "../enums/image-type-enum";
import {PageableDefault} from "../models/pageableDefault";
import {UserResponse} from "../interface/userResponse";

@Injectable({
  providedIn: 'root'
})
export class UserCardService {

  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {
  }

  createCard(userCardSaveRequest: CreateUserCard): Observable<any> {
    return this.http.post(`${this.apiUrl}/user/card/`, userCardSaveRequest);
  }

  uploadImage(formData: FormData, userCardId: bigint): Observable<any> {
    return this.http.post(`${this.apiUrl}/user/card/upload/${userCardId}`, formData);
  }

  updateImage(formData: FormData, userCardId: bigint): Observable<any> {
    return this.http.post(`${this.apiUrl}/user/card/update/image/${userCardId}`, formData);
  }

  getUsernameByQrUsername(qrUsername: string) {
    return this.http.get(`${this.apiUrl}/user/card/get-username/qrUsername/${qrUsername}`, { responseType: 'text' });
  }

  findAll(userId: bigint): Observable<any> {
    return this.http.get(`${this.apiUrl}/user/card/findAll?userId=${userId}`);
  }

  updateStatus(id: bigint): Observable<any> {
    return this.http.put(`${this.apiUrl}/user/card/status/${id}`, {});
  }

  generateQRCode(id: bigint): Observable<any> {
    return this.http.get(`${this.apiUrl}/user/card/${id}/generate`, {responseType: 'blob'});
  }

  getCardByQRCode(cardId: string): Observable<UserCardResponse> {
    return this.http.get<UserCardResponse>(`${this.apiUrl}/user/card/cardId/${cardId}`);
  }

  deleteCardById(id: bigint): Observable<any> {
    return this.http.delete(`${this.apiUrl}/user/card/${id}`);
  }

  editCardById(id: bigint, userCard: UserCard): Observable<any> {
    const url = `${this.apiUrl}/user/card/${id}`;
    return this.http.put(url, userCard);
  }

  findById(id: string): Observable<UserCardResponse> {
    return this.http.get<UserCardResponse>(`${this.apiUrl}/user/card/${id}`)
  }

  saveContactAsVCard(userCardId: bigint): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/user/card/generate-contact/${userCardId}`, {
      responseType: 'blob'
    });
  }

  searchUserCards(query: string, page: number, size: number): Observable<UserCardResponse[]> {
    return this.http.get<{content: UserCardResponse[]}>(`${this.apiUrl}/user/card/search`, {
      params: {query: query, page: page.toString(), size: size.toString()}
    }).pipe(map(response => response.content || []));
  }

  uploadDocument(formData: FormData, userCardContentId: bigint) {
    return this.http.put(`${this.apiUrl}/user/card/upload/document/${userCardContentId}`, formData)
  }

  changeUserCardContentView(cardId: bigint, isContentViewGrid: boolean): Observable<UserCardResponse> {
    return this.http.put<UserCardResponse>(`${this.apiUrl}/user/card/content-view/cardId/${cardId}?isContentViewGrid=${isContentViewGrid}`, {});
  }

  getCardImage(userCardId: bigint, type: ImageType) {
    return `${this.apiUrl}/user/card/${userCardId}/image?type=${type}`
  }

  searchCardsByTeamId(teamId: number, pageableDefault: PageableDefault): Observable<any> {
    let params: string = `?id=${teamId.toString()}`;

    if (pageableDefault.page || pageableDefault.page == 0) {
      params = params + "&page=" + pageableDefault.page;
    }

    if (pageableDefault.size) {
      params = params + "&size=" + pageableDefault.size;
    }

    if (pageableDefault.sort) {
      params = params + "&sort=" + pageableDefault.sort;
      if (pageableDefault.direction) {
        params = params + "," + pageableDefault.direction;
      }
    }

    const url = `${this.apiUrl}/user/card/team/search` + params;
    return this.http.get<any>(url);
  }

  getAllUserCards(page: number, size: number): Observable<any> {
    const params = new HttpParams()
      .set('page', page)
      .set('size', size);
    return this.http.get<any>(`${this.apiUrl}/user/card/all`, {params});
  }

  editCardId(id: bigint, cardId: string) {
    const url = `${this.apiUrl}/user/card/edit-username/${id}`;
    return this.http.put<void>(url, {cardId});
  }

  editCommissionById(id: bigint, commission: number) {
    const url = `${this.apiUrl}/user/card/id/${id}/commission/${commission}`;
    return this.http.patch(url, {id});
  }

  changeActivationStatus(teamId: bigint): Observable<any> {
    const params = new HttpParams().set('id', teamId.toString());
    return this.http.put<any>(`${this.apiUrl}/user/card/activity/change`, {}, {params});
  }

  checkIsUserSubscriptionValidForCreatingCard(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/user/card/check-user-subscription`);
  }

  checkImageExists(url: string): Observable<boolean> {
    return this.http.get(url, {observe: 'response', responseType: 'blob'}).pipe(
      map((response: HttpResponse<Blob>) => !!(response.body?.size)),
      catchError(() => of(false))
    );
  }

  findAllAdditionalCardsByUser(userId: bigint): Observable<UserCardResponse[]> {
    return this.http.get<UserCardResponse[]>(`${this.apiUrl}/user/card/additional/all?userId=${userId}`);
  }

  deleteCardsByIdes(selectedCardIds: bigint[]): Observable<any> {
    const ids = selectedCardIds.join(',');
   return  this.http.delete(`${this.apiUrl}/user/card/delete-by-ids?ids=${ids}`);
  }

  moveToSubscription(selectedCardIds: bigint[], id: bigint): Observable<any> {
    return this.http.put(`${this.apiUrl}/user/card/move-to-subscription/${id}`, selectedCardIds);
  }

  getUserCardsCount(): Observable<bigint> {
    return this.http.get<bigint>(`${this.apiUrl}/user/card/count`);
  }

  duplicate(id: number): Observable<UserCardResponse> {
    return this.http.post<UserCardResponse>(`${this.apiUrl}/user/card/duplicate/${id}`, null);
  }

  updateIsBusiness(userId: bigint, cardId: String, isBusiness: boolean): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/user/card/update-is-business/${userId}/${cardId}`, isBusiness);
  }
}
